import React, { useMemo } from "react";
import { ResponsiveBar } from "@nivo/bar";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { formatRsePercentage, takeTopRseRows } from "../reporteSaludEmergenciaUtils";

const TopComunidadesChart = ({ data, loading, error, showProjectCode = true }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));
  const sourceRows = data?.rows;
  const chartData = useMemo(() => {
    const rows = Array.isArray(sourceRows) ? sourceRows : [];
    const topRows = takeTopRseRows(rows, "atenciones", 10);

    return topRows.map((row, index) => {
      const name = row.comunidad || "Sin dato";
      const projectCode = row.codigo || "";

      return {
        rankingId: `${row.projectId ?? "all"}|${name}|${index}`,
        label: [name, showProjectCode ? projectCode : ""].filter(Boolean).join(" · "),
        name,
        projectCode,
        atenciones: Number(row.atenciones),
        porcentaje: row.porcentajeAtencionesProyecto,
      };
    });
  }, [showProjectCode, sourceRows]);
  const labels = useMemo(
    () => new Map(chartData.map((row) => [row.rankingId, row.label])),
    [chartData]
  );
  const leftAxisMargin = isSmall ? 120 : isMedium ? 175 : 220;

  return (
    <Paper
      sx={{
        width: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
        p: { xs: 2, md: 2.5 },
        height: "100%",
      }}
      data-testid="comunidades-panel"
    >
      <Typography variant="h5" sx={{ fontWeight: 850 }}>
        Top 10 comunidades por atenciones
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Comunidades con mayor volumen dentro del alcance seleccionado.
      </Typography>

      {error ? (
        <Alert severity="error">No fue posible cargar esta información.</Alert>
      ) : loading ? (
        <Stack minHeight={380} alignItems="center" justifyContent="center" spacing={1.5}>
          <CircularProgress aria-label="Cargando comunidades" />
          <Typography color="text.secondary">Cargando comunidades...</Typography>
        </Stack>
      ) : chartData.length === 0 ? (
        <Box minHeight={380} display="flex" alignItems="center" justifyContent="center" textAlign="center">
          <Typography color="text.secondary">No hay datos disponibles para este período.</Typography>
        </Box>
      ) : (
        <>
          <Box height={400} minWidth={0}>
            <ResponsiveBar
              data={chartData}
              keys={["atenciones"]}
              indexBy="rankingId"
              layout="horizontal"
              margin={{ top: 8, right: 30, bottom: 42, left: leftAxisMargin }}
              padding={0.28}
              colors={["#008080"]}
              borderRadius={3}
              enableLabel={false}
              axisTop={null}
              axisRight={null}
              axisLeft={{ tickSize: 0, tickPadding: 8, format: (value) => labels.get(value) || value }}
              axisBottom={{ tickSize: 0, tickPadding: 8, tickValues: 5 }}
              theme={{
                axis: { ticks: { text: { fill: theme.palette.text.secondary, fontSize: 11 } } },
                tooltip: { container: { color: theme.palette.text.primary } },
              }}
              tooltip={({ value, data: row }) => (
                <Paper sx={{ p: 1.25, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Comunidad: {row.name}
                  </Typography>
                  {row.projectCode && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Proyecto: {row.projectCode}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    Atenciones: {Number(value).toLocaleString("es-VE")}
                  </Typography>
                  {row.porcentaje !== null && row.porcentaje !== undefined && (
                    <Typography variant="caption">
                      Porcentaje: {formatRsePercentage(row.porcentaje)}
                    </Typography>
                  )}
                </Paper>
              )}
              role="application"
              ariaLabel="Top comunidades"
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Mostrando las 10 comunidades con mayor número de atenciones. El Excel contiene el detalle completo.
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default TopComunidadesChart;
